import { Link, useParams, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Activity, LogIn, UserPlus, Lock } from "lucide-react";
import AppLayout from "../components/AppLayout";
import ProfileHeader from "../components/ProfileHeader";
import CommunityActivityFeed from "../components/CommunityActivityFeed";
import BadgesCard from "../components/BadgesCard";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { toast } from "sonner";

/**
 * Public read-only profile at /u/:userId (accepts a Mongo ObjectId OR a claimed handle).
 * - Authenticated viewers see the full AppLayout + ProfileHeader.
 * - Anonymous viewers get a minimal branded header when the admin has enabled
 *   `public_profiles_enabled`. When disabled → bounced to /login.
 * - Injects OpenGraph + Twitter meta tags via react-helmet-async so links pasted
 *   to X/LinkedIn/Discord show a proper preview card.
 */
export default function PublicProfile() {
  const { userId } = useParams();
  const { user, loading } = useAuth();
  const { settings, loaded: settingsLoaded } = useSettings();
  const [data, setData] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const shouldFetch = user || settings?.public_profiles_enabled;

  useEffect(() => {
    if (loading || !settingsLoaded) return;
    if (!shouldFetch) { setFetching(false); return; }
    let cancelled = false;
    (async () => {
      setFetching(true);
      try {
        const { data } = await api.get(`/users/${userId}/profile`);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) { setNotFound(true); toast.error(formatApiError(e)); }
      } finally {
        if (!cancelled) setFetching(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId, loading, settingsLoaded, shouldFetch]);

  if (loading || !settingsLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-slate-500 uppercase tracking-[0.3em] text-xs font-display" data-testid="public-profile-loading">Loading</div>
      </div>
    );
  }

  if (!user && !settings?.public_profiles_enabled) {
    return <Navigate to="/login" replace />;
  }

  const siteName = settings?.site_name || "The Amino Exchange";
  const tagline = settings?.tagline || "Precision Dose Protocol";
  const brand = settings?.primary_color || "#0D9488";
  const pageUrl = typeof window !== "undefined" ? window.location.href : "";
  const description = data
    ? `${data.name} on ${siteName} — Level ${data.level?.level ?? 0} · ${data.stats?.shots ?? 0} shots logged · ${data.stats?.days_active ?? 0} days active. ${tagline}.`
    : `${tagline} — a private, secure peptide-protocol tracker.`;
  const ogTitle = data ? `${data.name}${data.handle ? ` (@${data.handle})` : ""} · ${siteName}` : siteName;
  const ogImage = data?.avatar_url || settings?.logo_url || "";

  const meta = (
    <Helmet prioritizeSeoTags>
      <title>{ogTitle}</title>
      <meta name="description" content={description} />
      {/* OpenGraph */}
      <meta property="og:type" content="profile" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={description} />
      {pageUrl && <meta property="og:url" content={pageUrl} />}
      {ogImage && <meta property="og:image" content={ogImage} />}
      {data?.name && <meta property="profile:username" content={data.handle || data.name} />}
      {/* Twitter / X */}
      <meta name="twitter:card" content={ogImage ? "summary_large_image" : "summary"} />
      <meta name="twitter:title" content={ogTitle} />
      <meta name="twitter:description" content={description} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}
    </Helmet>
  );

  // Logged-in viewer → normal chrome
  if (user) {
    return (
      <>
        {meta}
        <AppLayout>
          {data?.is_private ? (
            <PrivateStub data={data} />
          ) : (
            <>
              <ProfileHeader userId={userId} data={data} />
              {data?.badges?.length > 0 && (
                <BadgesCard badges={data.badges} displayName={data.name} />
              )}
              {data && (
                <CommunityActivityFeed userId={data.id} displayName={data.name} />
              )}
            </>
          )}
        </AppLayout>
      </>
    );
  }

  // Anonymous + enabled → minimal branded shell
  return (
    <>
      {meta}
      <div className="min-h-screen bg-white text-slate-900 noise-overlay" data-testid="public-profile-anon">
        <header className="border-b border-slate-200 bg-white/80 backdrop-blur-sm sticky top-0 z-30">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 group" data-testid="public-brand-logo">
              {settings?.logo_url ? (
                <div className="w-9 h-9 border border-slate-300 overflow-hidden flex items-center justify-center bg-slate-50">
                  <img src={settings.logo_url} alt={siteName} className="w-full h-full object-contain" />
                </div>
              ) : (
                <div className="w-9 h-9 border border-slate-300 flex items-center justify-center" style={{ backgroundColor: `${brand}1a` }}>
                  <Activity className="w-4 h-4" style={{ color: brand }} strokeWidth={2.5} />
                </div>
              )}
              <div className="flex flex-col leading-none">
                <span className="font-display font-black text-lg tracking-tight uppercase">{siteName}</span>
                <span className="font-mono-num text-[10px] text-slate-500 uppercase tracking-[0.25em] mt-0.5">{tagline}</span>
              </div>
            </Link>
            <div className="flex items-center gap-2">
              <Link to="/login" data-testid="public-profile-login"
                className="inline-flex items-center gap-1.5 border border-slate-200 hover:border-[color:var(--brand,#0D9488)] hover:text-[color:var(--brand,#0D9488)] text-slate-800 px-3 h-9 text-xs uppercase tracking-widest font-mono-num transition-colors">
                <LogIn className="w-3.5 h-3.5" /> Sign in
              </Link>
              <Link to="/signup" data-testid="public-profile-signup"
                className="inline-flex items-center gap-1.5 bg-[color:var(--brand,#0D9488)] hover:opacity-90 text-black px-3 h-9 text-xs uppercase tracking-widest font-semibold">
                <UserPlus className="w-3.5 h-3.5" /> Join
              </Link>
            </div>
          </div>
        </header>

        <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 relative z-10">
          {fetching ? (
            <div className="border border-slate-200 bg-white p-10 text-slate-500 text-sm animate-pulse">Loading profile…</div>
          ) : data?.is_private ? (
            <PrivateStub data={data} />
          ) : (
            <>
              <ProfileHeader userId={userId} data={data} />
              {data?.badges?.length > 0 && (
                <BadgesCard badges={data.badges} displayName={data.name} />
              )}
            </>
          )}
          <div className="mt-8 border border-slate-200 bg-white p-6 text-center" data-testid="public-profile-cta">
            <div className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-2">// Want your own?</div>
            <div className="font-display font-black uppercase tracking-tight text-2xl mb-3">Start tracking your protocol.</div>
            <p className="text-slate-600 text-sm max-w-xl mx-auto mb-5">
              {siteName} is a private, secure dashboard for logging your peptide protocols, computing your next dose,
              and reviewing your history at a glance. Free to try.
            </p>
            <Link to="/signup"
              className="inline-flex items-center gap-2 bg-[color:var(--brand,#0D9488)] hover:opacity-90 text-black px-5 h-11 text-sm uppercase tracking-widest font-semibold"
              data-testid="public-profile-cta-signup">
              <UserPlus className="w-4 h-4" /> Create your free account
            </Link>
          </div>
        </main>
      </div>
    </>
  );
}

function PrivateStub({ data }) {
  const initials = (data?.name || "?").split(" ").slice(0, 2).map((s) => s[0] || "").join("").toUpperCase();
  return (
    <div className="mb-8" data-testid="public-profile-private-stub">
      <div className="border border-slate-200 bg-white p-8 sm:p-10 flex flex-col items-center text-center">
        <div className="w-20 h-20 border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden mb-4">
          {data?.avatar_url ? (
            <img src={data.avatar_url} alt={data.name} className="w-full h-full object-cover opacity-70" />
          ) : (
            <span className="font-display font-black text-2xl text-slate-400">{initials}</span>
          )}
        </div>
        <div className="flex items-center gap-2 mb-2">
          <Lock className="w-4 h-4 text-[color:var(--brand,#0D9488)]" />
          <div className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-[color:var(--brand,#0D9488)]">// Private profile</div>
        </div>
        <div className="font-display font-black uppercase tracking-tight text-3xl text-slate-900 mb-1">{data?.name || "This user"}</div>
        {data?.handle && (
          <div className="font-mono-num text-[11px] text-slate-500 tracking-widest mb-4">@{data.handle}</div>
        )}
        <p className="text-slate-600 text-sm max-w-md">
          This profile is private. Only they and site staff can view their activity, stats, and posts.
        </p>
      </div>
    </div>
  );
}
